/**
 * format.js — formatação pt-BR dos números do visual Sphere.
 *
 * Regra: ausente/ inválido → "—" (nunca 0 fictício nem NaN); zero é zero.
 * Datas no fuso da APLICAÇÃO (`VITE_APP_TIMEZONE`, padrão America/Sao_Paulo —
 * a implantação é BR), não no fuso do navegador nem no do servidor.
 */

export const APP_TIMEZONE = (import.meta.env?.VITE_APP_TIMEZONE) || 'America/Sao_Paulo';
export const VAZIO = '—';

/** Número finito (aceita string numérica); senão null. */
export function numeroOuNulo(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** US$ 1.234,56 — o custo de IA é sempre em dólar (Wallet). */
export function usd(v) {
  const n = numeroOuNulo(v);
  if (n == null) return VAZIO;
  return `US$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** 1.234 */
export function inteiro(v) {
  const n = numeroOuNulo(v);
  if (n == null) return VAZIO;
  return Math.round(n).toLocaleString('pt-BR');
}

/** Tokens compactos: 950 · 23,40K · 390,50M · 1,20B */
export function tokensCompactos(v) {
  const n = numeroOuNulo(v);
  if (n == null) return VAZIO;
  const f = (x) => x.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (Math.abs(n) >= 1e9) return `${f(n / 1e9)}B`;
  if (Math.abs(n) >= 1e6) return `${f(n / 1e6)}M`;
  if (Math.abs(n) >= 1e3) return `${f(n / 1e3)}K`;
  return Math.round(n).toLocaleString('pt-BR');
}

/** Variação percentual com sinal: +100% · −12% · null → "—" */
export function variacao(v) {
  const n = numeroOuNulo(v);
  if (n == null) return VAZIO;
  const abs = Math.abs(n).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  if (n > 0) return `+${abs}%`;
  if (n < 0) return `−${abs}%`;
  return '0%';
}

/** dd/MM HH:mm no fuso da aplicação (ISO com fuso). Inválido → "—". */
export function dataHora(iso, timeZone = APP_TIMEZONE) {
  if (!iso) return VAZIO;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return VAZIO;
  const p = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone,
  }).formatToParts(d);
  const v = (t) => p.find((x) => x.type === t)?.value ?? '';
  return `${v('day')}/${v('month')} ${v('hour')}:${v('minute')}`;
}

/** "YYYY-MM" do instante no fuso da aplicação. */
export function mesAtual(agora = new Date(), timeZone = APP_TIMEZONE) {
  const p = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', timeZone }).formatToParts(agora);
  const y = p.find((x) => x.type === 'year')?.value;
  const m = p.find((x) => x.type === 'month')?.value;
  return `${y}-${m}`;
}

const MESES_CURTOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MESES_LONGOS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** "2026-09" → "Set" (curto) ou "setembro de 2026" (longo). */
export function rotuloDoMes(yyyymm, longo = false) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(yyyymm ?? ''));
  if (!m) return VAZIO;
  const i = Number(m[2]) - 1;
  if (i < 0 || i > 11) return VAZIO;
  return longo ? `${MESES_LONGOS[i]} de ${m[1]}` : MESES_CURTOS[i];
}

/**
 * Consumo do orçamento. Gasto ou limite ausente/ inválido (limite ≤ 0, NaN,
 * infinito, negativo) → indisponível (ratio null) — nunca NaN, ∞ ou 0% fictício.
 * `fill` é limitado a 100; `ratio` mostra o efetivo, inclusive acima de 100.
 */
export function consumoDoOrcamento(gasto, limite) {
  const g = numeroOuNulo(gasto);
  const l = numeroOuNulo(limite);
  if (g == null || l == null || g < 0 || l <= 0) return { ratio: null, fill: 0, state: 'unknown' };
  const ratio = (g / l) * 100;
  if (!Number.isFinite(ratio)) return { ratio: null, fill: 0, state: 'unknown' };
  return {
    ratio,
    fill: Math.min(100, ratio),
    state: ratio > 100 ? 'exceeded' : ratio >= 85 ? 'warning' : 'normal',
  };
}
